import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import {
  Zap,
  LogIn,
  HelpCircle,
  Mail,
  Phone,
  MapPin,
  Send,
  MessageSquare,
  Users,
  Home,
  Menu,
  ArrowRight,
  Globe,
  DollarSign
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { BrandMark } from './BrandMark';

const GlassCard: React.FC<{ children: React.ReactNode; className?: string; hover?: boolean }> = ({ children, className = '', hover = true }) => (
    <div className={`bg-white/5 backdrop-blur-3xl border border-white/10 rounded-3xl p-8 transition-all duration-500 overflow-hidden relative ${hover ? 'hover:bg-white/10 hover:border-white/20 hover:-translate-y-2 hover:shadow-[0_0_40px_rgba(59,130,246,0.1)] group' : ''} ${className}`}>
        {children}
    </div>
);

export const Contact = () => {
  const navigate = useNavigate();
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Comm established:', formData);
    // Handle form submission logic
  };

  return (
    <div className="min-h-screen bg-[#0a0b14] text-white flex flex-col relative overflow-hidden">
      {/* Ambient Neural Network Background */}
      <div className="fixed inset-0 z-0 pointer-events-none">
        <div className="absolute top-1/4 right-[10%] w-[500px] h-[500px] bg-blue-600/5 blur-[120px] rounded-full" />
        <div className="absolute bottom-1/4 left-[10%] w-[400px] h-[400px] bg-indigo-600/5 blur-[100px] rounded-full" />
        <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-[0.03] mix-blend-overlay" />
      </div>

      {/* Premium Navbar */}
      <nav className="sticky top-0 z-50 bg-[#0a0b14]/80 backdrop-blur-2xl border-b border-white/5 px-6 py-4">
          <div className="max-w-7xl mx-auto flex justify-between items-center">
              <div className="flex items-center gap-4 group cursor-pointer" onClick={() => navigate('/')}>
                  <BrandMark className="w-[86px] h-[48px] group-hover:scale-105 transition-transform duration-500 drop-shadow-[0_0_14px_rgba(59,130,246,0.45)]" />
                 <div className="hidden sm:block">
                  <p className="text-2xl font-black tracking-[-0.02em] text-blue-100/95 leading-none">3RDI</p>
                  <p className="text-[10px] uppercase tracking-[0.24em] text-blue-100/45 mt-1 font-semibold">Vision Intelligence Platform</p>
                 </div>
              </div>

              <div className="hidden md:flex items-center gap-2">
                  <Button variant="ghost" onClick={() => navigate('/')} className="text-blue-100/60 hover:text-white hover:bg-white/5 transition-all px-4 py-2 rounded-xl">
                      <Home className="h-4 w-4 mr-2" /> Home
                  </Button>
                  <Button variant="ghost" onClick={() => navigate('/about')} className="text-blue-100/60 hover:text-white hover:bg-white/5 transition-all px-4 py-2 rounded-xl">
                      <Users className="h-4 w-4 mr-2" /> About Us
                  </Button>
                  <Button variant="ghost" className="bg-white/5 text-white px-4 py-2 rounded-xl">
                      <Mail className="h-4 w-4 mr-2 text-blue-400" /> Contact
                  </Button>
                  <Button variant="ghost" onClick={() => navigate('/pricing')} className="text-blue-100/60 hover:text-white hover:bg-white/5 transition-all px-4 py-2 rounded-xl">
                      <DollarSign className="h-4 w-4 mr-2" /> Pricing
                  </Button>
                  <div className="h-6 w-[1px] bg-white/10 mx-2" />
                  <Button variant="ghost" onClick={() => navigate('/support')} className="text-blue-100/60 hover:text-white px-4 hover:bg-white/5 rounded-xl">Support</Button>
                  <button
                      onClick={() => navigate('/login')}
                      className="bg-blue-600 hover:bg-blue-500 text-white px-6 py-2.5 rounded-xl font-bold text-sm transition-all shadow-[0_0_20px_rgba(37,99,235,0.3)] hover:scale-105 active:scale-95"
                  >
                      <span className="flex items-center gap-2"><LogIn className="w-4 h-4" /> Sign In</span>
                  </button>
              </div>

              <button onClick={() => setShowMobileMenu(!showMobileMenu)} className="md:hidden p-2 text-gray-400 hover:bg-white/5 rounded-xl transition-colors">
                  <Menu />
              </button>
          </div>
      </nav>

      {/* Mobile Menu Dropdown */}
      <AnimatePresence>
        {showMobileMenu && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="fixed inset-x-0 top-[73px] z-40 bg-[#0a0b14]/95 backdrop-blur-3xl border-b border-white/10 p-6 md:hidden flex flex-col gap-4"
          >
            <button onClick={() => { navigate('/'); setShowMobileMenu(false); }} className="flex items-center gap-4 text-blue-100/60 p-4 rounded-xl hover:bg-white/5 transition-all"><Home className="w-5 h-5"/> Home</button>
            <button onClick={() => { navigate('/about'); setShowMobileMenu(false); }} className="flex items-center gap-4 text-blue-100/60 p-4 rounded-xl hover:bg-white/5 transition-all"><Users className="w-5 h-5"/> About Us</button>
            <button onClick={() => setShowMobileMenu(false)} className="flex items-center gap-4 text-white bg-white/5 p-4 rounded-xl transition-all"><Mail className="w-5 h-5 text-blue-400"/> Contact</button>
            <button onClick={() => { navigate('/pricing'); setShowMobileMenu(false); }} className="flex items-center gap-4 text-blue-100/60 p-4 rounded-xl hover:bg-white/5 transition-all"><DollarSign className="w-5 h-5"/> Pricing</button>
            <hr className="border-white/5 my-2" />
            <button onClick={() => { navigate('/support'); setShowMobileMenu(false); }} className="flex items-center gap-4 text-blue-100/60 p-4 rounded-xl hover:bg-white/5 transition-all"><HelpCircle className="w-5 h-5"/> Support</button>
            <button onClick={() => { navigate('/login'); setShowMobileMenu(false); }} className="w-full bg-blue-600 py-4 rounded-2xl font-bold mt-4 flex justify-center items-center gap-2"><LogIn className="w-5 h-5" /> Login / Sign Up</button>
          </motion.div>
        )}
      </AnimatePresence>

      <main className="flex-1 relative z-10 px-6 py-24 pb-32">
        <div className="max-w-6xl mx-auto space-y-24">
          
          {/* Neural Hero */}
          <div className="text-center space-y-8 max-w-4xl mx-auto">
             <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="inline-flex items-center justify-center p-4 rounded-3xl bg-blue-500/10 border border-blue-500/20 mb-4 shadow-[0_0_40px_rgba(59,130,246,0.3)]">
                 <Mail className="w-12 h-12 text-blue-400 flex items-center justify-center" />
             </motion.div>
             <h1 className="text-5xl md:text-7xl font-black text-white uppercase tracking-tighter">
                Secure <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-indigo-500">Comm</span> Link
             </h1>
             <p className="text-xl md:text-2xl text-blue-100/60 font-light leading-relaxed">
                Establish a direct connection with our deployment specialists and cognitive architecture team.
             </p>
             <div className="flex justify-center gap-8 md:gap-12 text-[10px] font-black uppercase tracking-[0.2em] text-blue-100/40 pt-12 border-t border-white/5">
                <div className="text-center">
                   <div className="text-4xl font-mono text-white mb-2 drop-shadow-[0_0_10px_rgba(255,255,255,0.3)]">&lt; 1h</div>
                   <div>Triage Time</div>
                </div>
                <div className="text-center">
                   <div className="text-4xl font-mono text-emerald-400 mb-2 drop-shadow-[0_0_10px_rgba(52,211,153,0.3)]">24/7</div>
                   <div>Grid Support</div>
                </div>
                <div className="text-center">
                   <div className="text-4xl font-mono text-purple-400 mb-2 drop-shadow-[0_0_10px_rgba(192,132,252,0.3)]">98%</div>
                   <div>Clearance Rate</div>
                </div>
             </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
            {/* Communication Portal */}
            <div className="lg:col-span-3">
               <GlassCard hover={false} className="h-full border-blue-500/20 shadow-[0_0_50px_rgba(59,130,246,0.1)]">
                 <h2 className="text-2xl font-black uppercase tracking-tight flex items-center gap-3 mb-8">
                    <Send className="w-6 h-6 text-blue-400" /> Transmit Inquiry
                 </h2>
                 <form onSubmit={handleSubmit} className="space-y-6 relative z-10">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                       <div className="space-y-2">
                          <label className="text-[10px] font-black uppercase tracking-widest text-blue-100/60">Operator Designation</label>
                          <input
                            type="text"
                            name="name"
                            placeholder="Full Name/Rank"
                            value={formData.name}
                            onChange={handleInputChange}
                            className="w-full bg-black/40 border border-white/10 text-white placeholder:text-white/20 py-4 px-4 rounded-2xl focus:outline-none focus:border-blue-500/50 transition-colors"
                            required
                          />
                       </div>
                       <div className="space-y-2">
                          <label className="text-[10px] font-black uppercase tracking-widest text-blue-100/60">Secure Origin (Email)</label>
                          <input
                            type="email"
                            name="email"
                            placeholder="secure@domain.com"
                            value={formData.email}
                            onChange={handleInputChange}
                            className="w-full bg-black/40 border border-white/10 text-white placeholder:text-white/20 py-4 px-4 rounded-2xl focus:outline-none focus:border-blue-500/50 transition-colors"
                            required
                          />
                       </div>
                    </div>

                    <div className="space-y-2">
                       <label className="text-[10px] font-black uppercase tracking-widest text-blue-100/60">Transmission Vector</label>
                       <select
                         name="subject"
                         value={formData.subject}
                         onChange={handleInputChange}
                         className="w-full bg-black/40 border border-white/10 text-white py-4 px-4 rounded-2xl focus:outline-none focus:border-blue-500/50 transition-colors appearance-none"
                         required
                       >
                         <option value="" className="bg-[#0f111a] text-blue-100/40">Select Operational Request</option>
                         <option value="technical" className="bg-[#0f111a] text-white">Technical Grid Support</option>
                         <option value="sales" className="bg-[#0f111a] text-white">Enterprise Deployment Specs</option>
                         <option value="partnership" className="bg-[#0f111a] text-white">Strategic Partnership Alliance</option>
                         <option value="general" className="bg-[#0f111a] text-white">General Inquiry</option>
                       </select>
                    </div>

                    <div className="space-y-2">
                       <label className="text-[10px] font-black uppercase tracking-widest text-blue-100/60">Intelligence Packet</label>
                       <textarea
                         name="message"
                         placeholder="Detail your request parameters here..."
                         value={formData.message}
                         onChange={handleInputChange}
                         rows={6}
                         className="w-full bg-black/40 border border-white/10 text-white placeholder:text-white/20 py-4 px-4 rounded-2xl focus:outline-none focus:border-blue-500/50 transition-colors resize-none"
                         required
                       />
                    </div>

                    <Button type="submit" className="w-full py-6 rounded-2xl bg-blue-600 hover:bg-blue-500 text-white font-black text-[10px] uppercase tracking-widest transition-all shadow-[0_0_30px_rgba(37,99,235,0.4)]">
                       <Send className="w-4 h-4 mr-3" /> Initiate Transmission
                    </Button>
                 </form>
               </GlassCard>
            </div>

            {/* Direct Links */}
            <div className="lg:col-span-2 space-y-8 flex flex-col">
               <GlassCard hover={false} className="flex-1 border-t-2 border-t-emerald-500/50">
                  <h2 className="text-2xl font-black uppercase tracking-tight flex items-center gap-3 mb-8">
                     <MessageSquare className="w-6 h-6 text-emerald-400" /> Direct Feeds
                  </h2>
                  <div className="space-y-8">
                     <div className="flex items-center gap-5 group cursor-not-allowed">
                        <div className="w-14 h-14 rounded-2xl bg-blue-500/10 border border-blue-500/20 flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                           <Mail className="w-6 h-6 text-blue-400" />
                        </div>
                        <div>
                           <h3 className="text-[10px] font-black uppercase tracking-widest text-blue-100/60 mb-1">Architecture Desk</h3>
                           <p className="font-mono text-white">support@csense.ai</p>
                        </div>
                     </div>
                     <div className="flex items-center gap-5 group cursor-not-allowed">
                        <div className="w-14 h-14 rounded-2xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                           <Phone className="w-6 h-6 text-emerald-400" />
                        </div>
                        <div>
                           <h3 className="text-[10px] font-black uppercase tracking-widest text-emerald-100/60 mb-1">Voice Uplink</h3>
                           <p className="font-mono text-white">+1 (555) 123-4567</p>
                        </div>
                     </div>
                     <div className="flex items-center gap-5 group cursor-not-allowed">
                        <div className="w-14 h-14 rounded-2xl bg-purple-500/10 border border-purple-500/20 flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                           <MapPin className="w-6 h-6 text-purple-400" />
                        </div>
                        <div>
                           <h3 className="text-[10px] font-black uppercase tracking-widest text-purple-100/60 mb-1">Command HQ</h3>
                           <p className="font-light text-sm text-white leading-relaxed">123 Innovation Dr.<br/>Tech Valley, CA 94000</p>
                        </div>
                     </div>
                  </div>
               </GlassCard>

               <div className="flex gap-4">
                  <Button className="flex-1 py-6 rounded-2xl bg-blue-600/10 border border-blue-500/30 hover:bg-blue-600 hover:border-blue-500 text-white font-black text-[10px] uppercase tracking-widest transition-all">
                     <Mail className="w-4 h-4 mr-2" /> Dispatch Email
                  </Button>
                  <Button className="flex-1 py-6 rounded-2xl bg-emerald-600/10 border border-emerald-500/30 hover:bg-emerald-600 hover:border-emerald-500 text-white font-black text-[10px] uppercase tracking-widest transition-all">
                     <Phone className="w-4 h-4 mr-2" /> Patch Call
                  </Button>
               </div>
            </div>
          </div>

          <GlassCard className="bg-gradient-to-r from-blue-900/20 to-purple-900/20 border-t-2 border-blue-500/40 text-center py-16">
              <h2 className="text-3xl md:text-4xl font-black uppercase tracking-tighter text-white mb-6">Require Immediate Triage?</h2>
              <p className="text-lg text-blue-100/60 max-w-2xl mx-auto font-light mb-8">
                 Access our comprehensive knowledge base for tactical guides, system configuration parameters, and deep troubleshooting of the 3RDI platform.
              </p>
              <Button className="bg-white/10 hover:bg-white/20 border border-white/20 text-white px-10 py-6 rounded-2xl font-black text-[10px] uppercase tracking-widest transition-all">
                 Access Archives <ArrowRight className="h-4 w-4 ml-3" />
              </Button>
          </GlassCard>

          {/* Sectors */}
          <div className="pt-12 text-center">
             <h2 className="text-4xl font-black uppercase tracking-tighter mb-12">Global Forward Bases</h2>
             <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-left">
                <div className="p-8 rounded-3xl bg-black/40 border border-white/5 backdrop-blur-xl relative overflow-hidden group">
                   <div className="absolute top-0 right-0 w-32 h-32 bg-blue-500/10 rounded-full blur-[40px] opacity-0 group-hover:opacity-100 transition-opacity"/>
                   <div className="w-12 h-12 rounded-xl bg-blue-500/10 border border-blue-500/20 flex items-center justify-center mb-6">
                      <Globe className="w-6 h-6 text-blue-400" />
                   </div>
                   <h3 className="text-2xl font-black uppercase tracking-tight mb-4">Malaysia Area</h3>
                   <div className="text-blue-100/60 font-mono text-xs leading-loose">
                      Level 20, Menara Binjai<br/>2 Jalan Binjai, 50450<br/>Kuala Lumpur
                   </div>
                </div>
                <div className="p-8 rounded-3xl bg-black/40 border border-white/5 backdrop-blur-xl relative overflow-hidden group">
                   <div className="absolute top-0 right-0 w-32 h-32 bg-emerald-500/10 rounded-full blur-[40px] opacity-0 group-hover:opacity-100 transition-opacity"/>
                   <div className="w-12 h-12 rounded-xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center mb-6">
                      <Globe className="w-6 h-6 text-emerald-400" />
                   </div>
                   <h3 className="text-2xl font-black uppercase tracking-tight mb-4">Singapore Area</h3>
                   <div className="text-emerald-100/60 font-mono text-xs leading-loose">
                      One Marina Boulevard<br/>Downtown Core Area<br/>Singapore 018989
                   </div>
                </div>
                <div className="p-8 rounded-3xl bg-black/40 border border-white/5 backdrop-blur-xl relative overflow-hidden group">
                   <div className="absolute top-0 right-0 w-32 h-32 bg-purple-500/10 rounded-full blur-[40px] opacity-0 group-hover:opacity-100 transition-opacity"/>
                   <div className="w-12 h-12 rounded-xl bg-purple-500/10 border border-purple-500/20 flex items-center justify-center mb-6">
                      <Globe className="w-6 h-6 text-purple-400" />
                   </div>
                   <h3 className="text-2xl font-black uppercase tracking-tight mb-4">Australia Area</h3>
                   <div className="text-purple-100/60 font-mono text-xs leading-loose">
                      100 Mount Street<br/>North Sydney NSW 2060<br/>Australia
                   </div>
                </div>
             </div>
          </div>
        </div>
      </main>
      
      <footer className="py-12 border-t border-white/5 text-center mt-auto relative z-10 bg-black/40 backdrop-blur-xl">
          <p className="text-blue-100/20 text-xs font-black uppercase tracking-[0.3em]">
              © 2026 3RDI VISION INTELLIGENCE
          </p>
           <p className="text-blue-100/10 text-[9px] font-black uppercase tracking-widest mt-2 hover:text-blue-500/50 transition-colors cursor-pointer">
              POWERED BY BeAItle DEFENSE ALGORITHMS
          </p>
      </footer>
    </div>
  );
};